import { ReactElement, useCallback } from 'react';

import {
  TertiaryButton,
  TertiaryButtonProps,
} from '@changegps/horizon-component-library';
import { CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import {
  useCheckGoCardlessStatusQuery,
  useConnectGoCardless,
  useDisconnectGoCardless,
} from '../hooks';

type ConnectDisconnectGoCardlessButtonProps = TertiaryButtonProps;

export const ConnectDisconnectGoCardlessButton = (
  props: ConnectDisconnectGoCardlessButtonProps,
): ReactElement => {
  const { mutate: connect, isPending: isConnecting } = useConnectGoCardless();
  const { mutate: disconnectGoCardless, isPending: isDisconnecting } =
    useDisconnectGoCardless();

  const goCardlessStatusQuery = useCheckGoCardlessStatusQuery();

  const handleConnect = useCallback(() => {
    connect(null, {
      onSuccess: (data: { authorize_url: string }) => {
        window.location.replace(data.authorize_url);
      },
      onError: () => {
        toast.error('Error connecting GoCardless');
      },
    });
  }, [connect]);

  const handleDisconnect = useCallback(() => {
    disconnectGoCardless(null, {
      onSuccess: () => {
        void goCardlessStatusQuery.refetch();
        toast.success('GoCardless disconnected');
      },
      onError: () => {
        toast.error('Error disconnecting GoCardless');
      },
    });
  }, [disconnectGoCardless, goCardlessStatusQuery]);

  const isConnected = Boolean(goCardlessStatusQuery.data?.isConnected);

  return (
    <TertiaryButton
      {...props}
      onClick={() => {
        isConnected ? handleDisconnect() : handleConnect();
      }}
    >
      <Content
        isLoading={isConnecting || isDisconnecting}
        isConnected={isConnected}
      />
    </TertiaryButton>
  );
};

type ContentProps = {
  isConnected: boolean;
  isLoading: boolean;
};

const Content = (props: ContentProps): ReactElement => {
  if (props.isLoading) return <CircularProgress size={16} />;

  if (props.isConnected) return <Typography fontSize={13}>Disable</Typography>;

  return <Typography fontSize={13}>Enable</Typography>;
};
